import { Node as PMNode } from "prosemirror-model";
import { Plugin, PluginKey, EditorState, Transaction, TextSelection, Selection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

export interface ProtectedNodeConfig {
  nodeName: string;
  allowEmptyDeletion?: boolean;
  isEmpty?: (node: PMNode) => boolean;
}

/**
 * Transaction meta that exempts a programmatic full-document replacement
 * (clear, template load, store->editor sync) from node protection. User
 * gestures can never set this, unlike the old "any ReplaceStep spanning the
 * whole doc" exemption, which select-all + paste/cut also matched — letting
 * two everyday gestures silently destroy protected media containers.
 */
export const CONTENT_RESET_META = "contentReset";

// --- Helper functions ---

interface NodePosition {
  pos: number;
  size: number;
  node: PMNode;
}

interface DeleteRange {
  from: number;
  to: number;
}

function findNodesInRange(state: EditorState, from: number, to: number, nodeName: string): NodePosition[] {
  const positions: NodePosition[] = [];
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type.name === nodeName) {
      positions.push({ pos, size: node.nodeSize, node });
    }
  });
  return positions;
}

function calculateDeletionRanges(from: number, to: number, protectedPositions: NodePosition[]): DeleteRange[] {
  const ranges: DeleteRange[] = [];
  const sortedPositions = [...protectedPositions].sort((a, b) => a.pos - b.pos);

  if (sortedPositions.length === 0) {
    if (from < to) ranges.push({ from, to });
    return ranges;
  }

  const firstNode = sortedPositions[0];
  if (firstNode && from < firstNode.pos) {
    ranges.push({ from, to: firstNode.pos });
  }

  for (let i = 0; i < sortedPositions.length - 1; i++) {
    const current = sortedPositions[i];
    const next = sortedPositions[i + 1];
    if (current && next) {
      const rangeStart = current.pos + current.size;
      const rangeEnd = next.pos;
      if (rangeStart < rangeEnd) {
        ranges.push({ from: rangeStart, to: rangeEnd });
      }
    }
  }

  const lastNode = sortedPositions[sortedPositions.length - 1];
  if (lastNode && lastNode.pos + lastNode.size < to) {
    ranges.push({ from: lastNode.pos + lastNode.size, to });
  }

  return ranges;
}

function deleteRangesExceptNodes(tr: Transaction, ranges: DeleteRange[]): Transaction {
  const sortedRanges = [...ranges].sort((a, b) => b.from - a.from);
  for (const range of sortedRanges) {
    tr.delete(range.from, range.to);
  }
  return tr;
}

/** Number of protected nodes in the doc, ignoring ones whose deletion is allowed. */
function countProtectedNodes(doc: PMNode, config: ProtectedNodeConfig): number {
  let count = 0;
  doc.descendants((node) => {
    if (node.type.name === config.nodeName && !(config.allowEmptyDeletion && config.isEmpty?.(node))) {
      count++;
    }
  });
  return count;
}

/**
 * Replace [from, to] with `text` while keeping any protected nodes inside the
 * range intact: the deletable stretches around the nodes are removed, the text
 * (if any) is inserted at the nearest valid position to the selection start,
 * and the caret lands after it. This is what Backspace over a media-spanning
 * selection always did — typing and pasting route through the same behavior so
 * the three gestures stay consistent.
 *
 * Returns false (untouched view) when the range contains no protected node,
 * so callers can fall through to their default handling.
 */
export function replaceRangeAroundProtectedNodes(
  view: EditorView,
  from: number,
  to: number,
  text: string,
  protectedNodes: ProtectedNodeConfig[],
): boolean {
  const { state } = view;

  const nodesInRange = protectedNodes.map((config) => findNodesInRange(state, from, to, config.nodeName)).flat();
  if (nodesInRange.length === 0) {
    return false;
  }

  const tr = state.tr;
  deleteRangesExceptNodes(tr, calculateDeletionRanges(from, to, nodesInRange));

  // Land at the nearest valid caret position to the (mapped) selection start.
  const mappedFrom = Math.min(tr.mapping.map(from, -1), tr.doc.content.size);
  let selection = Selection.near(tr.doc.resolve(mappedFrom), 1);

  if (!(selection instanceof TextSelection)) {
    // Every textblock was inside the selection (e.g. select-all in a doc that
    // is just text + media). Append an empty paragraph so the text has a home
    // — the trailing-node extension would add one anyway, but only after this
    // transaction has already dispatched.
    const paragraph = state.schema.nodes.paragraph?.createAndFill();
    if (!paragraph) {
      return true; // nothing sane to do; swallow rather than destroy the node
    }
    tr.insert(tr.doc.content.size, paragraph);
    selection = TextSelection.create(tr.doc, tr.doc.content.size - 1);
  }

  tr.setSelection(selection);
  if (text) {
    tr.insertText(text);
  }
  view.dispatch(tr);
  return true;
}

export function createNodeProtectionPlugin(protectedNodes: ProtectedNodeConfig[]): Plugin {
  return new Plugin({
    key: new PluginKey("nodeProtection"),

    filterTransaction(tr, state) {
      if (!tr.docChanged) return true;

      // Allow force delete transactions (from programmatic deletion)
      if (tr.getMeta("forceDelete") === true) {
        return true;
      }

      // Allow explicit programmatic full-document replacements (clear,
      // template load, store sync) — tagged via CONTENT_RESET_META.
      if (tr.getMeta(CONTENT_RESET_META) === true) {
        return true;
      }

      // Allow undo/redo — they only ever revert transactions that were
      // themselves allowed through this filter.
      if (tr.getMeta("history$")) {
        return true;
      }

      // A transaction may not reduce the number of protected nodes. Counting
      // (rather than inspecting steps) lets attribute updates through — e.g.
      // setNodeMarkup on a leaf node emits a ReplaceStep that fully covers
      // the node but re-inserts it — while still blocking every gesture that
      // would actually destroy one (cut, paste-over, drag, input rules).
      for (const config of protectedNodes) {
        if (countProtectedNodes(state.doc, config) > countProtectedNodes(tr.doc, config)) {
          return false;
        }
      }

      return true;
    },

    props: {
      handleTextInput: (view, from, to, text) => {
        if (from === to) {
          return false; // plain typing replaces nothing
        }
        // Typing over a selection that spans a protected node: delete around
        // the node and insert the text, instead of silently swallowing the
        // keystroke.
        return replaceRangeAroundProtectedNodes(view, from, to, text, protectedNodes);
      },

      handleKeyDown: (view, event) => {
        if (event.key !== "Backspace" && event.key !== "Delete") {
          return false;
        }

        const { state } = view;
        const { selection } = state;
        const { $from, $to, empty } = selection;

        if (!empty && replaceRangeAroundProtectedNodes(view, $from.pos, $to.pos, "", protectedNodes)) {
          event.preventDefault();
          return true;
        }

        const nodeBefore = $from.nodeBefore;
        const nodeAfter = $to.nodeAfter;

        for (const config of protectedNodes) {
          if (event.key === "Backspace" && nodeBefore?.type.name === config.nodeName) {
            if (!config.allowEmptyDeletion || !config.isEmpty?.(nodeBefore)) {
              event.preventDefault();
              return true;
            }
          }

          if (event.key === "Delete" && nodeAfter?.type.name === config.nodeName) {
            if (!config.allowEmptyDeletion || !config.isEmpty?.(nodeAfter)) {
              event.preventDefault();
              return true;
            }
          }
        }

        return false;
      },
    },
  });
}
